import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import CircularProgress from '@/components/common/CircularProgress';
import { redeemLoginToken } from '../hooks/api/loginToken';
import { saveSession } from '../hooks/api/auth/tokenManager';
import { markForeignSessionIfNeeded } from './foreignSession';
import { currentDeviceOwner } from './pushDevice';
import { hardNavigate } from './hardNavigate';
import { PUSH_LINK_MESSAGE, parsePushLink } from './pushLink';

export { LOGIN_LINK_PARAM } from './pushLink';

type GateState = 'idle' | 'redeeming' | 'failed';

/**
 * Procesa los links con código de acceso (`?acceso=<código>`), que llegan de dos formas:
 *  - **App cerrada:** la notificación abre la URL directamente → se lee de `window.location` al montar.
 *  - **App ya abierta (o en segundo plano):** el service worker le manda un mensaje `psi-push-link` con la URL.
 *    Hace falta porque en iOS `client.navigate()` no recarga la PWA instalada y el código nunca se veía.
 *
 * En los dos casos: se saca el código de la URL ANTES de canjearlo (es de un solo uso: recargar no lo reusa ni queda
 * en el historial), se guarda la sesión y se RECARGA la app — `PsychologistContext` solo restaura sus datos al
 * montarse, y sin recargar quedaría en memoria la psicóloga anterior. Un link sin código solo navega.
 */
export function PwaLoginLinkGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const inFlightRef = useRef<Set<string>>(new Set());
  const [state, setState] = useState<GateState>(() =>
    parsePushLink(window.location.href, window.location.origin)?.code ? 'redeeming' : 'idle',
  );

  const redeem = useCallback((code: string, path: string) => {
    // Un mismo código puede llegar dos veces (URL + mensaje): se canjea una sola.
    if (inFlightRef.current.has(code)) return;
    inFlightRef.current.add(code);
    setState('redeeming');

    redeemLoginToken(code)
      .then((session) => {
        // De quién es este celular (quién activó las push acá), ANTES de pisar la sesión.
        const deviceOwner = currentDeviceOwner();
        saveSession(session.token, session.user, session.scopes);
        // Si el código es de OTRA persona ("Entrar como una psicóloga" desde psima), esta sesión no puede tocar las push del celular.
        markForeignSessionIfNeeded(deviceOwner);
        // Igual que el impersonate de la web: no arrastrar la foto/token de Google de la sesión anterior.
        try {
          localStorage.removeItem('google_user');
          localStorage.removeItem('google_access_token');
        } catch {
          /* noop */
        }
        hardNavigate(path);
      })
      .catch(() => setState('failed'));
  }, []);

  // App cerrada: el código viene en la URL con la que se abrió.
  useEffect(() => {
    const link = parsePushLink(window.location.href, window.location.origin);
    if (!link?.code) return;
    window.history.replaceState(null, '', link.path);
    redeem(link.code, link.path);
  }, [redeem]);

  // App abierta: el service worker avisa con un mensaje.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null;
      if (data?.type !== PUSH_LINK_MESSAGE) return;

      // Confirmación inmediata: sin ella el service worker asume que esta versión no sabe procesar el link.
      event.ports?.[0]?.postMessage('ok');

      const link = parsePushLink(data.url ?? '', window.location.origin);
      if (!link) return;
      if (link.code) redeem(link.code, link.path);
      else navigate(link.path);
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate, redeem]);

  if (state === 'redeeming') {
    return (
      <main className="flex min-h-[100dvh] items-center justify-center bg-[#F3EFFF]">
        <CircularProgress label="Ingresando" />
      </main>
    );
  }

  if (state === 'failed') {
    return (
      <main className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-[#F3EFFF] px-6 text-center font-[Poppins,sans-serif] text-[#455375]">
        <h1 className="text-xl font-semibold text-[#2d2049]">El enlace ya no es válido</h1>
        <p className="max-w-xs text-sm">Venció o ya se usó. Ingresá con tu cuenta de Google para continuar.</p>
        <Button
          variant="primary"
          onClick={() => {
            setState('idle');
            navigate('/login', { replace: true });
          }}
        >
          Ir al ingreso
        </Button>
      </main>
    );
  }

  return <>{children}</>;
}
